import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Checkbox, Chip, CircularProgress, TextField } from "@mui/material";
import paymentportalservice from "../../../services/paymentportalservice";

// Optional multi-select of the internal user(s) who sold the campaign. The
// form owns the selection as a plain array of ids, like ShareWithDropdown.
// `error` is a message from the form (e.g. the API rejecting an inactive user);
// it's cleared by the form's re-validation as soon as the selection changes.
const SalespersonDropdown = ({ value = [], onChange, disabled = false, error = null }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    paymentportalservice
      .FetchSalespersonUsers()
      .then((data) => {
        if (cancelled) return;
        setUsers(data);
        setFetchError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setUsers([]);
        setFetchError(error?.response?.data || error.message || "Failed to fetch users");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(
    () =>
      users.map((user) => ({
        value: String(user._id),
        label: user.fullName || "Unnamed user",
        caption: [user.email, user.role].filter(Boolean).join(" · "),
      })),
    [users]
  );

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value]
  );

  return (
    <Box sx={{ width: 350 }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        id="salesperson-select"
        options={options}
        loading={loading}
        disabled={disabled || Boolean(fetchError)}
        value={selectedOptions}
        onChange={(event, newValue) => onChange(newValue.map((option) => option.value))}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        noOptionsText="No users available"
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value}>
            <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 14, fontWeight: 600 }}>{option.label}</Box>
              {option.caption && (
                <Box sx={{ fontSize: 12, opacity: 0.7 }}>{option.caption}</Box>
              )}
            </Box>
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              size="small"
              label={option.label}
              {...getTagProps({ index })}
              key={option.value}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Salesperson"
            error={Boolean(fetchError || error)}
            helperText={fetchError || error || "Optional — select one or more"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default SalespersonDropdown;
